import { /*Map,*/ fromJS } from "immutable";
import { handleActions, createAction } from "redux-actions";
import Restful from "lib/restful";
import GaHelper from "lib/gaHelper";
import config from "lib/fr-config";

const UNIT_LENGTH = config.outfitList.LENGTH;
const EXP_MINUTE = 30;

const SET_OUTFIT_LIST = "myOutfit/SET_OUTFIT_LIST";
const SET_EXPIRATION_DATE = "myOutfit/SET_EXPIRATION_DATE";
const CHECK_IS_END = "myOutfit/CHECK_IS_END";
const SET_IS_LOADING = "myOutfit/SET_IS_LOADING";
const REMOVE_OUTFIT = "myOutfit/REMOVE_OUTFIT";
const SET_NEED_REALTIME_DATA = "myOutfit/SET_NEED_REALTIME_DATA";
const SET_CAN_WRITE_DATA_NOW = "myOutfit/SET_CAN_WRITE_DATA_NOW";
const INIT_MUTABLE_DATA = "myOutfit/INIT_MUTABLE_DATA";
const INIT_ALL_DATA = "myOutfit/INIT_ALL_DATA";

const setOutfitList = createAction(SET_OUTFIT_LIST);
const setExpirationDate = createAction(SET_EXPIRATION_DATE);
const checkIsEnd = createAction(CHECK_IS_END);
const setIsLoading = createAction(SET_IS_LOADING);
const removeOutfit = createAction(REMOVE_OUTFIT);
const setNeedRealtimeData = createAction(SET_NEED_REALTIME_DATA);
const setCanWriteDataNow = createAction(SET_CAN_WRITE_DATA_NOW);
const initMutableData = createAction(INIT_MUTABLE_DATA);
const initAllData = createAction(INIT_ALL_DATA);

const getMyOutfitState = ({ getState }) => {
  const myOutfitState = getState().myOutfit.toJS();
  return myOutfitState;
};

const getIsLoading = ({ getState }) => {
  const myOutfitState = getMyOutfitState({ getState });
  const { isLoading } = myOutfitState;
  return isLoading;
};

const getLastHash = list => {
  if (list.length === 0) return undefined;
  const length = list.length;
  return list[length - 1]["outfitHash"];
};

const deleteAllDataWhenLoggedOut = ({ dispatch, getState }) => {
  const { canWriteDataNow } = getMyOutfitState({ getState });
  if (!canWriteDataNow) dispatch(initAllData());
};

const requestMyOutfitList = async ({
  needRealtimeData,
  outfitList,
  idToken
}) => {
  const length = UNIT_LENGTH;
  const lastHash = getLastHash(outfitList);
  const { code, res, errInfo } = await Restful.getMyOutfitAPI({
    idToken,
    lastHash,
    length,
    needRealtimeData
  });
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  } else {
    return { list: res.list };
  }
};

const checkExpDate = ({ getState }) => {
  const myOutfitState = getMyOutfitState({ getState });
  const { expirationDate } = myOutfitState;
  if (!expirationDate) return { needUpdate: true };
  const nowDate = new Date();
  return { needUpdate: nowDate > expirationDate ? true : false };
};

const setExpDate = ({ dispatch }) => {
  const expirationDate = new Date();
  expirationDate.setMinutes(expirationDate.getMinutes() + EXP_MINUTE);
  dispatch(setExpirationDate(expirationDate));
};

const concatMyOutfitList = async ({
  needRealtimeData = false,
  getState,
  dispatch,
  idToken
}) => {
  const myOutfitState = getMyOutfitState({ getState });
  const { outfitList } = myOutfitState;
  const { list } = await requestMyOutfitList({
    needRealtimeData,
    outfitList,
    idToken
  });
  dispatch(setOutfitList(outfitList.concat(list)));
  dispatch(checkIsEnd(list.length));
  GaHelper.setGARequestOutfitListEvent({
    type: "myOutfitList",
    pageNum: outfitList.length / UNIT_LENGTH + 1
  });
};

export const initList = idToken => async (dispatch, getState) => {
  dispatch(setCanWriteDataNow(true));

  const { needRealtimeData } = getMyOutfitState({ getState });
  let isLoading = getIsLoading({ getState });
  if (isLoading) return null;

  dispatch(setIsLoading(true));

  const { needUpdate } = checkExpDate({ getState });

  if (needUpdate) {
    dispatch(initMutableData());
    await concatMyOutfitList({ needRealtimeData, getState, dispatch, idToken });
    setExpDate({ dispatch });

    if (needRealtimeData) {
      dispatch(setNeedRealtimeData(false));
    }
  }

  dispatch(setIsLoading(false));
  deleteAllDataWhenLoggedOut({ dispatch, getState });
};

export const getList = idToken => async (dispatch, getState) => {
  const myOutfitState = getMyOutfitState({ getState });
  const { isEnd } = myOutfitState;
  const isLoading = getIsLoading({ getState });

  if (isLoading || isEnd) return null;

  dispatch(setIsLoading(true));
  await concatMyOutfitList({ getState, dispatch, idToken });
  dispatch(setIsLoading(false));
  deleteAllDataWhenLoggedOut({ dispatch, getState });
};

export const deleteOutfit =
  ({ outfitHash, idToken }) =>
  async (dispatch, getState) => {
    const { code, errInfo } = await Restful.removeMyOutfitListAPI({
      outfitHash,
      idToken
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      throw errObj;
    } else if (code === "OutfitDoesNotExist") {
      /** already deleted */
    }
    dispatch(removeOutfit(outfitHash));
  };

export const uploadOutfit =
  ({ filename, itemHashArray, calendarDate, calendarAgreement, idToken }) =>
  async (dispatch, getState) => {
    const { code, errInfo } = await Restful.setMyOutfitAPI({
      idToken,
      data: {
        file: filename,
        items: itemHashArray,
        date: calendarDate,
        display: !!calendarAgreement ? "y" : "n"
      }
    });
    if (code === "error") {
      const errObj = Error("api error");
      errObj.errorValue = { ...errInfo };
      throw errObj;
    }
  };

export const setRealtimeDataOnNextRequest = () => (dispatch, getState) => {
  dispatch(setNeedRealtimeData(true));
};

export const deleteAllData = () => (dispatch, getState) => {
  dispatch(initAllData());
};

const initialState = fromJS({
  outfitList: [],
  expirationDate: null,
  isEnd: false,
  isLoading: false,

  /**
   *  canWriteDataNow 는 로그아웃되어 데이터 초기화된 후에,
   * 딜레이된 요청으로 인해 로그아웃한 사용자의 데이터가 store 에
   * 쓰여지는 것을 방지하기 위한 변수.
   */
  canWriteDataNow: false,

  needRealtimeData: false
});

export default handleActions(
  {
    [SET_OUTFIT_LIST]: (state, action) => {
      const outfitList = action.payload;
      return state.set("outfitList", fromJS(outfitList));
    },
    [SET_EXPIRATION_DATE]: (state, action) => {
      const expirationDate = action.payload;
      return state.set("expirationDate", fromJS(expirationDate));
    },
    [CHECK_IS_END]: (state, action) => {
      const length = action.payload;
      const isEnd = length === UNIT_LENGTH ? false : true;
      return state.set("isEnd", fromJS(isEnd));
    },
    [SET_IS_LOADING]: (state, action) => {
      const isLoading = action.payload;
      return state.set("isLoading", fromJS(isLoading));
    },
    [REMOVE_OUTFIT]: (state, action) => {
      const outfitHash = action.payload;
      const { outfitList } = state.toJS();
      const newList = outfitList.filter(obj => obj.outfitHash !== outfitHash);
      return state.set("outfitList", fromJS(newList));
    },
    [SET_NEED_REALTIME_DATA]: (state, action) => {
      const needRealtimeData = action.payload;
      return state.set("needRealtimeData", fromJS(needRealtimeData));
    },
    [SET_CAN_WRITE_DATA_NOW]: (state, action) => {
      const canWriteDataNow = action.payload;
      return state.set("canWriteDataNow", fromJS(canWriteDataNow));
    },
    [INIT_MUTABLE_DATA]: (state, action) => {
      const { outfitList, expirationDate, isEnd } = initialState.toJS();
      return state
        .set("outfitList", fromJS(outfitList))
        .set("expirationDate", fromJS(expirationDate))
        .set("isEnd", fromJS(isEnd));
    },
    [INIT_ALL_DATA]: (state, action) => {
      return initialState;
    }
  },
  initialState
);
